"use client";

import { ANALYTICS_EVENTS, track } from "@/lib/analyticsEvents";
import { claimAudioPreview, releaseAudioPreview, stopAudioPreview } from "@/lib/audioPreviewController";
import { getAudioVoice } from "../../../shared/audio";
import { api } from "convex/_generated/api";
import { useAction } from "convex/react";
import { Loader2, Pause, Play } from "lucide-react";
import { useCallback, useState } from "react";

const previewCache = new Map<string, string>();
const previewRequests = new Map<string, Promise<string>>();

export function VoicePreviewButton({ voiceId }: { voiceId: string }) {
  const previewVoice = useAction(api.actions.previewAudioVoice.previewVoice);
  const [state, setState] = useState<"idle" | "loading" | "playing" | "error">("idle");
  const key = `voice-${voiceId}`;

  const toggle = useCallback(async () => {
    if (state === "playing") {
      stopAudioPreview(key);
      setState("idle");
      return;
    }
    const voice = getAudioVoice(voiceId);
    if (!voice) {
      setState("error");
      return;
    }
    setState("loading");
    try {
      let src = previewCache.get(voiceId);
      if (!src) {
        let request = previewRequests.get(voiceId);
        if (!request) {
          request = previewVoice({ voiceId }).then((result) => result.audioDataUrl);
          previewRequests.set(voiceId, request);
        }
        src = await request;
        previewCache.set(voiceId, src);
        previewRequests.delete(voiceId);
      }
      const audio = new Audio(src);
      claimAudioPreview(key, audio, () => setState("idle"));
      audio.addEventListener("ended", () => {
        releaseAudioPreview(key);
        setState("idle");
      }, { once: true });
      audio.addEventListener("error", () => setState("error"), { once: true });
      await audio.play();
      setState("playing");
      track(ANALYTICS_EVENTS.audioVoicePreviewed, {
        voice_name: voice.name,
        locale: voice.locale,
      });
    } catch {
      previewRequests.delete(voiceId);
      setState("error");
    }
  }, [key, previewVoice, state, voiceId]);

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      aria-label={state === "playing" ? "Pause voice preview" : "Preview this voice"}
      aria-pressed={state === "playing"}
      className="inline-flex h-11 shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success disabled:cursor-wait disabled:opacity-60"
      disabled={state === "loading"}
    >
      {state === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" /> : state === "playing" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      {state === "error" ? "Retry preview" : state === "playing" ? "Pause" : state === "loading" ? "Loading…" : "Preview"}
    </button>
  );
}
